import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';
import { cn } from '../lib/cn';
import { initialWords } from '../lib/initials';
/**
 * Falls back to initials rather than a silhouette. The design uses initials
 * tiles everywhere a photo is optional — the organizer profile without a logo
 * (`ORG-PRO-002`), the team card (`ADM-ORG-016`), a member with no photo — and
 * a generic person icon makes a populated list look like a list of strangers.
 */
/**
 * "Sofia Braga" → "SB". Two letters at most: three is a monogram, not a tile. Only letters and
 * numbers, so "Ann-Marie O'Neil" is "AO" (`ORG-031`). This is a person's; an organization's
 * initials are `organizationInitials`.
 */
export const initialsFrom = (name) => initialWords(name)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
const sizes = {
    sm: 'h-8 w-8 text-2xs',
    md: 'h-10 w-10 text-xs',
    lg: 'h-14 w-14 text-sm',
};
/**
 * **How the initials fallback is filled**, which the 10 September prototype draws two ways.
 *
 * `tint` is the one this component shipped with and the one every list uses: a soft brand disc
 * with brand-700 letters, which is what `Web App.dc.html` draws on a roster card, a match card and
 * a connection card — a face among many, where a saturated fill would make a list shout.
 *
 * `spark` is the **subject's own** disc: §2.15's 72px profile circle, `background:
 * var(--spark-gradient)` with white letters at `font-brand` 800, and the same treatment the
 * desktop header's avatar button uses. It is a prop rather than a `className` override because the
 * fallback is inside the primitive and cannot be reached from outside — which is how the account
 * page came to draw a tinted disc where the prototype draws the gradient, with nobody able to see
 * the difference in the markup.
 */
const tones = {
    tint: 'bg-brand-100 font-semibold text-brand-700',
    spark: 'bg-spark font-brand font-extrabold text-white',
};
export const Avatar = React.forwardRef(({ className, name, src, size = 'md', tone = 'tint', ...props }, ref) => (_jsxs(AvatarPrimitive.Root, { ref: ref, className: cn('relative flex shrink-0 overflow-hidden rounded-pill', sizes[size], className), ...props, children: [src ? _jsx(AvatarPrimitive.Image, { src: src, alt: name, className: "aspect-square h-full w-full object-cover" }) : null, _jsx(AvatarPrimitive.Fallback
        // aria-hidden because the accessible name comes from whatever labels the
        // row this sits in; announcing "SB" adds nothing and interrupts.
        , { "aria-hidden": true, className: cn('flex h-full w-full items-center justify-center uppercase tracking-wide', tones[tone]), children: initialsFrom(name) })] })));
Avatar.displayName = AvatarPrimitive.Root.displayName;
//# sourceMappingURL=avatar.js.map